import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { AINotification } from './ai-assistant.types';
import { EventDispatcherService } from '../../services/event-dispatcher/event-dispatcher.service';
import { WebsocketService } from '../../services/websocket/websocket.service';

const MAX_MESSAGES = 20;

@Injectable({ providedIn: 'root' })
export class AiAssistantService {
  private readonly messagesSubject = new BehaviorSubject<AINotification[]>([]);
  public readonly messages$: Observable<AINotification[]> = this.messagesSubject.asObservable();

  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  public readonly hasUnread$: Observable<boolean> = this.unreadCountSubject.pipe(
    map(count => count > 0),
    distinctUntilChanged()
  );

  private logoutSubscriptionId: string | null = null;

  constructor(
    private eventDispatcher: EventDispatcherService,
    private socket: WebsocketService
  ) {
    this.socket.connect();
    this.socket.subscribe('*', (payload: any) => {
      if (payload?.notificationType === 'AI_INSIGHT') {
        this.handleAIInsight(payload);
      }
    });
    this.logoutSubscriptionId = this.eventDispatcher.subscribeToEvent('logout', () => {
      this.handleLogout();
    });
  }

  private handleAIInsight(payload: any): void {
    const notification: AINotification = {
      id: payload.messageId || this.generateNotificationId(),
      message: payload.summary || payload.title || 'New AI insight received',
      timestamp: new Date(payload.createdAt || Date.now())
    };

    const messages = [notification, ...this.messagesSubject.value].slice(0, MAX_MESSAGES);
    this.messagesSubject.next(messages);
    this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
  }

  public injectNotification(message: string): void {
    this.handleAIInsight({
      notificationType: 'AI_INSIGHT',
      messageId: `injected_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      summary: message,
      createdAt: new Date().toISOString()
    });
  }

  public markAllRead(): void {
    this.unreadCountSubject.next(0);
  }

  private handleLogout(): void {
    this.messagesSubject.next([]);
    this.unreadCountSubject.next(0);
  }

  private generateNotificationId(): string {
    return `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  public destroy(): void {
    if (this.logoutSubscriptionId) {
      this.eventDispatcher.unsubscribeFromEvent(null, this.logoutSubscriptionId);
    }
  }
}
